/**
 * @internal
 */
export function fromHexString(hex) {
    let red = '0', green = '0', blue = '0';
    let opacity = '0xff';
    // 3 digits
    if (hex.length == 4) {
        red = '0x' + hex[1] + hex[1];
        green = '0x' + hex[2] + hex[2];
        blue = '0x' + hex[3] + hex[3];
        // 6 digits
    }
    else if (hex.length == 7) {
        red = '0x' + hex[1] + hex[2];
        green = '0x' + hex[3] + hex[4];
        blue = '0x' + hex[5] + hex[6];
    }
    else if (hex.length == 9) {
        red = '0x' + hex[1] + hex[2];
        green = '0x' + hex[3] + hex[4];
        blue = '0x' + hex[5] + hex[6];
        opacity = '0x' + hex[7] + hex[8];
    }
    return {
        red: parseInt(red),
        green: parseInt(green),
        blue: parseInt(blue),
        opacity: parseInt(opacity) / 255,
    };
}
function fromRGBString(rgb) {
    const sep = rgb.indexOf(',') > -1 ? ',' : ' ';
    const isRGBA = rgb.indexOf('rgba(') > -1;
    const [red, green, blue, opacity] = rgb
        .substr(isRGBA ? 5 : 4)
        .split(')')[0]
        .split(sep);
    return {
        red: parseInt(red),
        green: parseInt(green),
        blue: parseInt(blue),
        opacity: parseFloat(opacity) || 1,
    };
}
export function fromString(color) {
    if (typeof color === 'string') {
        color = color.trim();
        if (color.startsWith('rgba')) {
            return fromRGBString(color);
        }
        if (color.startsWith('rgb')) {
            return fromRGBString(color);
        }
        return fromHexString(color);
    }
    return color;
}
export function withOpacity(color, opacity) {
    const colorObject = fromString(color);
    return `rgba(${colorObject.red}, ${colorObject.green}, ${colorObject.blue}, ${opacity})`;
}
export function isLight(color) {
    const colorObject = fromString(color);
    const perceivedLightness = Math.sqrt(0.299 * Math.pow(colorObject.red, 2) +
        0.587 * Math.pow(colorObject.green, 2) +
        0.114 * Math.pow(colorObject.blue, 2));
    return perceivedLightness > 127.5;
}
/**
 * Darken
 * @param {number} color The color to darken
 * @param {number} amount The amount to darken between 0 and 1
 */
export function darken(color, amount) {
    const colorObject = fromString(color);
    return `rgba(
    ${Math.max(0, colorObject.red - 255 * amount)},
    ${Math.max(0, colorObject.green - 255 * amount)},
    ${Math.max(0, colorObject.blue - 255 * amount)},
    ${colorObject.opacity}
  )`;
}
/**
 * Lighten
 * @param {number} color The color to lighten
 * @param {number} amount The amount to lighten between 0 and 1
 */
export function lighten(color, amount) {
    const colorObject = fromString(color);
    return `rgba(
    ${Math.min(255, colorObject.red + 255 * amount)},
    ${Math.min(255, colorObject.green + 255 * amount)},
    ${Math.min(255, colorObject.blue + 255 * amount)},
    ${colorObject.opacity}
  )`;
}
/**
 * blend
 * @param {number} backgroundColor The background color to use
 * @param {number} color The color to use
 * @param {number} opacity The amount to merge between 0 and 1
 */
export function blend(backgroundColor, color, opacity) {
    const backgroundColorObject = fromString(backgroundColor);
    const colorObject = fromString(color);
    return `rgb(${Math.round((1 - opacity) * backgroundColorObject.red + opacity * colorObject.red)},
  ${Math.round((1 - opacity) * backgroundColorObject.green + opacity * colorObject.green)},
  ${Math.round((1 - opacity) * backgroundColorObject.blue + opacity * colorObject.blue)})`;
}
/**
 * Highlight
 * @param {number} amount The amount to highlight between 0 and 1
 */
export function highlight(color, amount) {
    return isLight(color) ? darken(color, amount) : lighten(color, amount);
}
var Colors;
(function (Colors) {
    Colors["black"] = "rgb(0, 0, 0)";
    Colors["white"] = "rgb(255, 255, 255)";
    /**
     * @deprecated Please use lightGrey as the correct name
     */
    Colors["pebbleGrey"] = "rgb(240, 240, 240)";
    Colors["lightGrey"] = "rgb(240, 240, 240)";
    Colors["signalWhite"] = "rgb(236, 236, 231)";
    Colors["greyWhite"] = "rgb(217, 217, 214)";
    Colors["ironGrey"] = "rgb(83, 86, 90)";
    Colors["stormGrey"] = "rgb(117, 120, 123)";
    Colors["greyChateau"] = "rgb(151, 153, 155)";
    Colors["agatheGrey"] = "rgb(177, 179, 179)";
    Colors["greyNurse"] = "rgb(200, 201, 199)";
    /**
     * Please use Colors.accessibleSafetyOrange when you can. See https://polestarjira.atlassian.net/wiki/spaces/PDGN/pages/1035902809
     */
    Colors["safetyOrange"] = "rgb(255, 117, 0)";
    Colors["accessibleSafetyOrange"] = "rgb(240, 110, 0)";
    Colors["parisDaisy"] = "rgb(247, 234, 72)";
    Colors["aquamarine"] = "rgb(89, 243, 253)";
    Colors["elementalBlue"] = "rgb(76, 148, 254)";
    Colors["elementalRed"] = "rgb(216, 15, 33)";
    Colors["darkGold"] = "rgb(134, 109, 75)";
    Colors["lightGold"] = "rgb(211, 188, 141)";
    Colors["error"] = "rgb(222, 45, 33)";
    Colors["info"] = "rgb(30, 136, 229)";
    Colors["success"] = "rgb(0, 230, 118)";
    Colors["transparent"] = "rgba(0, 0, 0, 0)";
})(Colors || (Colors = {}));
export default Colors;
